import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SurveyResponse, SurveyOption } from '@nx-c4g/c4g-models';
import { BaseSurveyStepComponent } from '../base-survey-step/base-survey-step.component';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'c4g-multiple-choice-multi-select-with-text-survey-step',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './multiple-choice-multi-select-with-text-survey-step.component.html',
  styleUrls: ['./multiple-choice-multi-select-with-text-survey-step.component.scss']
})
export class MultipleChoiceMultiSelectWithTextSurveyStepComponent extends BaseSurveyStepComponent {
  selectedOptionIds: string[] = [];
  textValues: { [optionId: string]: string } = {};
  currentPage = 0;
  readonly pageSize = 4;

  constructor(
    @Inject('stepData') stepData: any,
    @Inject('stepResponse') stepResponse: any,
    @Inject('initialStepResponse') initialStepResponse: any,
    public translate: TranslateService
  ) {
    super(stepData, stepResponse, initialStepResponse);
  }

  override ngOnInit() {
    super.ngOnInit();
    if (this.initialStepResponse && this.initialStepResponse.response) {
      this.selectedOptionIds = this.initialStepResponse.response.map((r: any) => r._id);
      for (const resp of this.initialStepResponse.response) {
        const opt = this.stepData?.options?.find((o: SurveyOption) => o._id === resp._id);
        if (opt && opt.inputType === 'text') {
          this.textValues[opt._id] = resp.values?.[0] || '';
        }
      }
    }
  }

  get pagedOptions(): SurveyOption[] {
    const start = this.currentPage * this.pageSize;
    return this.stepData?.options?.slice(start, start + this.pageSize) || [];
  }

  get allOptionsShown(): boolean {
    return (this.currentPage + 1) * this.pageSize >= (this.stepData?.options?.length || 0);
  }

  onToggleOption(option: SurveyOption) {
    const idx = this.selectedOptionIds.indexOf(option._id);
    if (idx > -1) {
      this.selectedOptionIds.splice(idx, 1);
      if (option.inputType === 'text') {
        delete this.textValues[option._id];
      }
    } else {
      this.selectedOptionIds.push(option._id);
      if (option.inputType === 'text' && !this.textValues[option._id]) {
        this.textValues[option._id] = '';
      }
    }
  }

  onSeeMore() {
    if (!this.allOptionsShown) {
      this.currentPage++;
    }
  }

  override isValid(): boolean {
    if (this.selectedOptionIds.length === 0 || !this.allOptionsShown) return false;
    for (const id of this.selectedOptionIds) {
      const opt = this.stepData?.options?.find((o: SurveyOption) => o._id === id);
      if (opt && opt.inputType === 'text') {
        if (!this.textValues[id] || this.textValues[id].trim().length < 3) {
          return false;
        }
      }
    }
    return true;
  }

  override async getResponse(): Promise<SurveyResponse | undefined> {
    if (!this.isValid()) return undefined;
    const selectedOptions = (this.stepData?.options || []).filter((opt: SurveyOption) => this.selectedOptionIds.includes(opt._id));
    return {
      stepId: this.stepData._id,
      showValueInReport: null,
      response: selectedOptions.map(opt => ({
        _id: opt._id,
        values: [opt.inputType === 'text' ? this.textValues[opt._id] : opt.value]
      }))
    };
  }

  getLangValue(mls: { en: string; fr: string }): string {
    const lang = this.translate.currentLang === 'fr' ? 'fr' : 'en';
    return mls?.[lang] || mls?.['en'] || '';
  }
}
